import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { getGameById } from '@/mocks/games';
import type { Article } from '@/mocks/articles';
import type { Game } from '@/mocks/games';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | undefined>();
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);
  const [relatedGames, setRelatedGames] = useState<Game[]>([]);

  useEffect(() => {
    if (slug) {
      const found = getArticleBySlug(slug);
      setArticle(found);
      if (found) {
        setRelatedArticles(getRelatedArticles(found.id));
        setRelatedGames(
          found.relatedGameIds
            .map((id) => getGameById(id))
            .filter(Boolean) as Game[]
        );
      }
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-14 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">文章未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，未找到该文章。</p>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium text-sm whitespace-nowrap">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: 'H游戏精选',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://xinchengtongxun.cn/article/${article.slug}`,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <article className="pt-24 pb-14">
          {/* Breadcrumb */}
          <div className="max-w-3xl mx-auto px-4 md:px-6 mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>

          {/* Article Header */}
          <header className="max-w-3xl mx-auto px-4 md:px-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">
                {article.category}
              </span>
              {article.tags.map((tag) => (
                <span key={tag} className="px-2 py-1 text-[11px] bg-secondary-100 text-secondary-600 rounded-full whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight text-balance">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readTime} 分钟阅读
              </span>
            </div>
          </header>

          {/* Cover Image */}
          <div className="max-w-3xl mx-auto px-4 md:px-6 mb-10">
            <div className="rounded-xl overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - h游戏攻略`}
                className="w-full aspect-[2/1] object-cover"
              />
            </div>
          </div>

          {/* Article Content */}
          <div className="max-w-3xl mx-auto px-4 md:px-6 mb-14">
            <div
              className="prose prose-sm md:prose-base max-w-none
                prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                prose-strong:text-foreground-900
                prose-li:text-foreground-700"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>

          {/* Related Games */}
          {relatedGames.length > 0 && (
            <section className="max-w-3xl mx-auto px-4 md:px-6 mb-14">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                相关游戏
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {relatedGames.map((game) => (
                  <Link
                    key={game.id}
                    to={`/category/${game.categorySlug}`}
                    className="flex items-center gap-4 p-3 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 transition-colors cursor-pointer group"
                  >
                    <img
                      src={game.coverImage}
                      alt={game.title}
                      className="w-16 h-16 rounded-lg object-cover shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {game.title}
                      </p>
                      <div className="flex items-center gap-1 mt-1">
                        <i className="ri-star-fill text-primary-500 text-[10px]"></i>
                        <span className="text-xs text-foreground-500">{game.rating}</span>
                        <span className="text-[11px] text-foreground-400 ml-2">{game.category}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="max-w-3xl mx-auto px-4 md:px-6 mb-14">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                相关文章
              </h2>
              <div className="space-y-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="flex flex-col sm:flex-row gap-4 p-4 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 transition-colors cursor-pointer group"
                  >
                    <img
                      src={ra.coverImage}
                      alt={ra.title}
                      className="w-full sm:w-32 h-20 object-cover rounded-lg shrink-0"
                    />
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 font-heading mb-1">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-1">{ra.excerpt}</p>
                      <p className="text-[11px] text-foreground-400 mt-2">{ra.publishDate} · {ra.author}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>

        <Footer />
      </div>
    </>
  );
}